import { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "baseui/icon";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuEye, LuPencil } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { SettingsIconButton } from "../../../components/button/SettingsIconButton";
import { SettingsDropdown } from "../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { ListPager } from "../../../components/pagination/ListPager";
import { DotsSvg } from "../../../components/svg/DotsSvg";
import { TrashSvg } from "../../../components/svg/TrashSvg";
import { Table } from "../../../components/table/Table";
import { updatePaymentStatusFactory } from "../../../factories/billing/factory-update-payment-status";
import { TimePeriod } from "../../../hooks/useDetectTimePeriod";
import { usePaymentFiltersController } from "../../../hooks/usePaymentFiltersController";
import {
  IPayment,
  SortEnum,
  useDeletePaymentMutation,
  useGetPaymentAmountsQuery,
  useGetPaymentsQuery,
  useGetPaymentStatusesQuery,
  useSetPaymentLabelMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { ExcelExport } from "../../../utils/ExportData";
import DeletePopup from "../DeletePopup";
import DropStatus from "../DropStatus";
import {
  BILLING_PRIMARY_BUTTON,
  ClientsCell,
  formatMoney,
  StatCard,
} from "../ui/BillingUi";
import { billingClientLink } from "../ui/invoiceStatus.utils";
import { PaymentsListFilters } from "./PaymentsListFilters";

const PAGE_SIZE = 20;

const formatCurrency = formatMoney;

// Table cell styles from the V49 design.
const HEAD_CELL =
  "px-[10px] py-2.5 text-[12.5px] font-bold text-[#8B92A2] first:pl-[22px] last:pr-[22px]";
const BODY_CELL =
  "bg-transparent px-[10px] py-3.5 text-[13px] text-[#3A4256] first:pl-[22px] last:pr-[22px]";
const FOOT_CELL = "px-[10px] py-3.5 first:pl-[22px] last:pr-[22px]";
const META = {
  headerClassName: HEAD_CELL,
  className: BODY_CELL,
  footerClassName: FOOT_CELL,
};

export const Payments: React.FC<unknown> = () => {
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId;

  const [page, setPage] = useState(1);
  const [activePaymentId, setActivePaymentId] = useState("");
  const [paymentDelete, setPaymentDelete] = useState<IPayment | null>(null);

  const [sort, setSort] = useState<{ name: SortEnum; order: boolean }>({
    name: SortEnum.PaymentDate,
    order: false,
  });

  const filters = usePaymentFiltersController();

  const isAllDateFilter =
    !filters.filter.period || filters.filter.period === TimePeriod.All;

  const currentFiltersKey = useMemo(
    () =>
      JSON.stringify({
        from: filters.from ? filters.from.toISOString() : null,
        to: filters.to ? filters.to.toISOString() : null,
        contacts: filters.filterByContact.map((item) => item.value),
        labels: filters.filterByLabel.map((item) => item.value),
      }),
    [filters.from, filters.to, filters.filterByContact, filters.filterByLabel],
  );

  const previousFiltersKeyRef = useRef(currentFiltersKey);

  useEffect(() => {
    if (previousFiltersKeyRef.current !== currentFiltersKey) {
      previousFiltersKeyRef.current = currentFiltersKey;
      setPage(1);
    }
  }, [currentFiltersKey]);

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetPaymentStatusesQuery(orgId || "");

  const {
    data: payments,
    isLoading,
    isFetching,
  } = useGetPaymentsQuery({
    orgId,
    pageNumber: page,
    pageSize: PAGE_SIZE,
    filterByDate: isAllDateFilter ? [] : ["Custom"],
    fromDate: isAllDateFilter
      ? null
      : filters.from
      ? filters.from.toISOString()
      : null,
    toDate: isAllDateFilter
      ? null
      : filters.to
      ? filters.to.toISOString()
      : null,
    sortBy: sort.name,
    sortOrder: sort.order ? "asc" : "desc",
    filterByStatus: [],
    filterByClient: filters.filterByContact.map((item) => item.value),
    filterByAssignee: [],
    filterByLabel: filters.filterByLabel.map((item) => item.value),
  });

  const [updatePayment, { isLoading: isLoadingUpdate }] =
    useSetPaymentLabelMutation();

  const [deletePayment] = useDeletePaymentMutation();

  const { data: amounts } = useGetPaymentAmountsQuery(orgId);

  const paymentsData = payments || [];

  const totalRecords = payments?.[0]?.totalRecords ?? 0;

  const handleSort = useCallback(
    (name: SortEnum) => {
      setPage(1);

      if (name === sort.name) {
        setSort({ name, order: !sort.order });
      } else {
        setSort({ name, order: true });
      }
    },
    [sort.name, sort.order],
  );

  const updateStatus = useCallback(
    (statusId: string, payment: IPayment) => {
      const body = updatePaymentStatusFactory.createModelFromDto({
        payment,
        status: statusId || null,
      });

      updatePayment(body);
    },
    [updatePayment],
  );

  const handleDeletePayment = useCallback((payment: IPayment) => {
    setPaymentDelete(payment);
  }, []);

  const renderSortIcon = useCallback(
    (name: SortEnum) => {
      if (sort.name !== name) {
        return null;
      }

      return (
        <span className={"ml-0.5 inline-flex text-[#6B4EFF]"}>
          {sort.order ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      );
    },
    [sort.name, sort.order],
  );

  const renderSortableHeader = useCallback(
    (label: string, sortName: SortEnum) => (
      <div
        className={
          "flex cursor-pointer items-center whitespace-nowrap normal-case"
        }
        onClick={() => handleSort(sortName)}>
        <div>{label}</div>
        {renderSortIcon(sortName)}
      </div>
    ),
    [handleSort, renderSortIcon],
  );

  const columns = useMemo<ColumnDef<IPayment, unknown>[]>(
    () => [
      {
        id: "customerName",
        header: () => renderSortableHeader("Contact", SortEnum.Customer),
        enableSorting: false,
        cell: ({ row }) => {
          const payment = row.original;

          return (
            <ClientsCell
              moreCount={payment?.customerCounts}
              clients={[
                {
                  name: payment?.customerName || "",
                  link: billingClientLink(payment || {}),
                  isOrganization: !!payment?.businessContactId,
                },
                ...(payment?.linkedCustomerNames ?? []).map((customer) => ({
                  name: customer.clientName,
                  isOrganization: customer.customerType === "Business",
                })),
              ]}
            />
          );
        },
        meta: {
          ...META,
          className: `${BODY_CELL} min-w-[220px] max-w-[340px]`,
        },
      },
      {
        id: "paymentDate",
        header: () =>
          renderSortableHeader("Payment date", SortEnum.PaymentDate),
        enableSorting: false,
        cell: ({ row }) =>
          moment(row.original.paymentDate).format("MMM DD, YYYY"),
        footer: () => (
          <div
            className={"text-end text-[13.5px] font-extrabold text-[#1F2433]"}>
            Total:
          </div>
        ),
        meta: META,
      },
      {
        id: "amount",
        header: () => renderSortableHeader("Amount", SortEnum.Amount),
        enableSorting: false,
        cell: ({ row }) => (
          <span className={"tabular-nums"}>
            {formatCurrency(row.original.amount)}
          </span>
        ),
        footer: () => (
          <div
            className={
              "text-[13.5px] font-extrabold tabular-nums text-[#1F2433]"
            }>
            {formatCurrency(payments?.[0]?.sumOfAmount)}
          </div>
        ),
        meta: META,
      },
      {
        id: "paymentMethodName",
        header: () => renderSortableHeader("Method", SortEnum.PaymentMethod),
        enableSorting: false,
        cell: ({ row }) => row.original.paymentMethodName,
        meta: META,
      },
      {
        id: "label",
        header: () => <div className={"whitespace-nowrap"}>Label</div>,
        enableSorting: false,
        cell: ({ row }) => {
          const payment = row.original;

          return (
            <DropStatus
              invoiceStatus={null}
              isLoading={
                (isLoadingUpdate || isFetching) &&
                payment?.paymentId === activePaymentId
              }
              paymentStatus={{
                name: payment?.paymentStatus?.statusName || "",
                id: payment?.paymentStatus?.statusId || "",
                color: payment?.paymentStatus?.statusColor || "",
              }}
              statuses={statuses || []}
              selectStatus={(status) => {
                setActivePaymentId(payment?.paymentId);
                updateStatus(status?.id || "", {
                  ...payment,
                  id: payment?.paymentId,
                });
              }}
            />
          );
        },
        meta: META,
      },
      {
        id: "notes",
        header: () => <div className={"whitespace-nowrap"}>Notes</div>,
        enableSorting: false,
        cell: ({ row }) => row.original.notes || "—",
        meta: {
          ...META,
          className: `${BODY_CELL} max-w-[300px] truncate whitespace-nowrap text-[#8B92A2]`,
        },
      },
      {
        id: "actions",
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => {
          const payment = row.original;

          return (
            <div
              className={"flex justify-end"}
              onClick={(event) => event.stopPropagation()}>
              <SettingsDropdown
                actionsList={[
                  {
                    name: "View",
                    action: () =>
                      navigate("/billing/payments/" + payment.paymentId),
                    icon: <LuEye />,
                  },
                  {
                    name: "Edit",
                    action: () =>
                      navigate(`/billing/payments/${payment.paymentId}/edit`),
                    icon: <LuPencil />,
                  },
                  {
                    name: "Delete",
                    action: () => handleDeletePayment(payment),
                    icon: <TrashSvg />,
                    extraClasses: "text-[#DC2626]",
                    iconColor: "text-[#DC2626]",
                  },
                ]}>
                <SettingsIconButton
                  icon={<DotsSvg />}
                  onClick={() => null}
                  size={"small"}
                />
              </SettingsDropdown>
            </div>
          );
        },
        meta: META,
      },
    ],
    [
      activePaymentId,
      handleDeletePayment,
      isFetching,
      isLoadingUpdate,
      payments,
      renderSortableHeader,
      statuses,
      updateStatus,
    ],
  );

  if (isLoading || filters.isLoadingContacts || isLoadingStatuses) {
    return <LottieLoading />;
  }

  return (
    <>
      <div className={"mb-[26px] max-w-[300px]"}>
        <StatCard
          label={"YTD payments received"}
          value={formatCurrency(amounts?.totalPayments)}
          hint={
            "Payments dated this year up to today (future-dated ones are left out)"
          }
        />
      </div>

      <div
        className={
          "rounded-[14px] border border-[#E8E8EC] bg-white shadow-[0_1px_2px_rgba(20,22,34,0.04)]"
        }>
        <div
          className={
            "flex flex-wrap items-end justify-between gap-3.5 px-[22px] pb-[18px] pt-5"
          }>
          <PaymentsListFilters controller={filters} />

          <div className={"flex items-center gap-3.5"}>
            <span
              title={"Export"}
              className={
                "flex h-[38px] w-[38px] items-center justify-center rounded-[9px] border border-[#DFE2E8] hover:bg-[#F4F2FF] [&_svg]:h-[20px] [&_svg]:w-[20px]"
              }>
              <ExcelExport
                data={
                  payments?.map((payment) => ({
                    Contact: payment?.customerName,
                    Amount: payment?.amount,
                    Method: payment?.paymentMethodName,
                    "Payment Date": moment(payment?.paymentDate).format(
                      "MMM DD, YYYY",
                    ),
                    Label: payment?.paymentStatus || "",
                    Notes: payment?.notes || "",
                  })) || []
                }
                fileName={"Payments"}
              />
            </span>

            <button
              type={"button"}
              onClick={() => navigate("./add")}
              className={BILLING_PRIMARY_BUTTON}>
              New payment
            </button>
          </div>
        </div>

        {isFetching ? (
          <LottieLoading />
        ) : (
          <>
            {/* Phones: the wide table scrolls inside its own box. */}
            <div className={"max-md:overflow-x-auto"}>
              <Table
                columns={columns}
                data={paymentsData}
                getRowId={(payment) => payment.paymentId}
                onRowClick={(payment) =>
                  navigate("/billing/payments/" + payment.paymentId)
                }
                rowClassName={
                  "cursor-pointer border-b border-[#F4F5F7] hover:bg-[#FAFAFC]"
                }
                theadClassName={
                  "border-y border-[#F0F1F4] text-[12.5px] font-bold text-[#8B92A2]"
                }
                showFooter={!!paymentsData.length}
                footerRowClassName={
                  "sticky bottom-0 left-0 border-t border-[#ECEEF2] bg-[#F6F7F9]"
                }
              />
            </div>

            {!!payments?.length && (
              <ListPager
                page={page}
                pageSize={PAGE_SIZE}
                total={totalRecords}
                disabled={isFetching}
                onChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {paymentDelete && (
        <DeletePopup
          isActive={!!paymentDelete}
          title={"Delete this payment?"}
          messages={[
            "This will delete the payment permanently and cannot be undone.",
            "The invoices it paid will show their balance again.",
          ]}
          onClose={() => setPaymentDelete(null)}
          onDelete={() => {
            // List rows carry paymentId (not id); the API only needs the id.
            void deletePayment({ id: paymentDelete.paymentId } as IPayment);
            setPaymentDelete(null);
          }}
        />
      )}
    </>
  );
};
